/**
 * What a provider is, as plain data plus pure functions. Nothing in here, and nothing a provider
 * file imports, may touch the network, the DOM or a clock: the registry runs in the browser, in
 * Node tests and in the Worker, and the edge verifies a phone's upload by running the very same
 * `parse` over bytes it fetched itself.
 */
import type { ELabelFacts, ELabelProviderId } from "../facts.js";

/**
 * How one fetch step reaches its server. `cors`: the phone fetches directly, because that
 * endpoint answers with access-control-allow-origin. `relay`: a browser may not read it, so the
 * phone asks the edge's byte relay. Decided per data endpoint, never per provider page.
 */
export type ELabelAccess = "cors" | "relay";

/** `honest` names proef; `mobile` is for a server that only serves phones (disclosed in the docs). */
export type ELabelUserAgentClass = "honest" | "mobile";

/**
 * `live`: the parser reads real data and the relay may fetch the provider. `stub`: the host is
 * recognised, so its link is never sent to druif, but nothing is fetched until a parser ships.
 */
export type ELabelProviderState = "live" | "stub";

/** A scanned link, recognised and rebuilt from checked parts, so nothing unchecked is ever requested. */
export interface ELabelTarget {
  /** Registry id of the provider that recognised it. */
  readonly provider: string;
  /**
   * Canonical https URL. It is also the upload proof, so for Scantrust it holds the api key:
   * never log it, store it or put it in an error.
   */
  readonly url: string;
  /** The provider's public record id from the link (safe to log), or "" when the link has none. */
  readonly ref: string;
  /** Checked parts the steps need (a uid, a key, a code). May hold secrets, like `url`. */
  readonly params: Readonly<Record<string, string>>;
}

export interface ELabelRequest {
  readonly method: "GET" | "POST";
  readonly url: string;
  /** Extra headers; `cors` steps only, because the relay never forwards a header. */
  readonly headers?: Readonly<Record<string, string>>;
  /** An application/x-www-form-urlencoded body; `cors` steps only. */
  readonly body?: string;
}

/** What one step returned: the URL it ended on (after redirects) and its body as text. */
export interface ELabelStepResult {
  readonly finalUrl: string;
  readonly text: string;
}

export interface ELabelStepContext {
  readonly target: ELabelTarget;
  /** The language this attempt asks for, or `undefined` for a provider without a language choice. */
  readonly lang: string | undefined;
  /** Earlier steps' results by step id, so a later request can use what an earlier one found. */
  readonly prior: Readonly<Record<string, ELabelStepResult>>;
}

/** A plain request/response step. A `relay` step must be a GET with no headers and no body. */
export interface ELabelFetchStep {
  readonly id: string;
  readonly kind: "fetch";
  readonly access: ELabelAccess;
  readonly request: (context: ELabelStepContext) => ELabelRequest;
}

/**
 * A redirect that must not be followed: only its `Location` is wanted (u-label's short link points
 * at a Scantrust viewer carrying the key). The result's `finalUrl` is that target and `text` is empty.
 */
export interface ELabelRedirectStep {
  readonly id: string;
  readonly kind: "redirect";
  readonly access: "relay";
  readonly request: (context: ELabelStepContext) => ELabelRequest;
}

export type ELabelStep = ELabelFetchStep | ELabelRedirectStep;

export interface ELabelParseInput {
  readonly target: ELabelTarget;
  /** The language the steps asked for. */
  readonly lang: string | undefined;
  /** Every step's result by step id. */
  readonly results: Readonly<Record<string, ELabelStepResult>>;
}

export interface ELabelProvider {
  /** Registry key, stable. Equal to `provider` except for an alias such as `u-label`. */
  readonly id: string;
  /** What `ELabel.provider` says of a label read through this entry. */
  readonly provider: ELabelProviderId;
  /** Shown to the user as written; a brand, so never translated. */
  readonly name: string;
  readonly state: ELabelProviderState;
  /**
   * Every host this provider may touch, exact, lowercase, https only: its entry links, redirect
   * hops and data endpoints. A host outside the list is refused by the relay and the phone alike.
   */
  readonly hosts: readonly string[];
  /** Recognises a scanned link (or a relay URL); `undefined` for anything not exactly ours. */
  readonly match: (text: string) => ELabelTarget | undefined;
  /**
   * For a provider whose data endpoints are not its entry link (a JSON API behind a JS page): the
   * target for one of those exact endpoint URLs, with `url` equal to the input, or `undefined`.
   * The relay accepts only what `match` or this returns, so it never fetches an unlisted path.
   */
  readonly matchRelay?: (text: string) => ELabelTarget | undefined;
  readonly needs: {
    readonly userAgent: ELabelUserAgentClass;
    /** Languages to ask for, most wanted first (nl, then en); absent when the page has no choice. */
    readonly languages?: readonly string[];
  };
  /** Fetch steps in order; each carries its own access. */
  readonly steps: readonly ELabelStep[];
  /**
   * Pure: bytes in, label out. Throws {@link ELabelParseError} (or anything) when the input is not
   * this provider's e-label; returns status `empty` when it is one the producer left blank.
   */
  readonly parse: (input: ELabelParseInput) => ELabelFacts;
}

/** Adapter for a provider whose label comes from one step's text: `parse: singleStep("page", fn)`. */
export function singleStep(
  stepId: string,
  parse: (text: string, finalUrl: string, input: ELabelParseInput) => ELabelFacts,
): (input: ELabelParseInput) => ELabelFacts {
  return (input) => {
    const result = input.results[stepId];
    if (result === undefined) throw new Error(`step ${stepId} has no result`);
    return parse(result.text, result.finalUrl, input);
  };
}
